/*
 * About Section – Sharon's story and experience
 * Asymmetric layout with photo on one side, text on the other
 * Botanical accents and watercolor background
 */

import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Award, Heart, Users, Sparkles } from "lucide-react";

const SHARON_TEACHING = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/SaronGoldberg_a477a543.png";
const ABOUT_BG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/OLDCOUPLEPRIVATEWORKOUT_c5b9185f.jpeg";

const stats = [
  { icon: Award, number: "30+", label: "שנות ניסיון" },
  { icon: Users, number: "1000+", label: "מתאמנות ומתאמנים" },
  { icon: Heart, number: "100%", label: "מסירות" },
  { icon: Sparkles, number: "∞", label: "מוטיבציה" },
];

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" className="relative py-24 md:py-36 overflow-hidden">
      {/* Subtle watercolor background */}
      <div className="absolute inset-0 opacity-30">
        <img src={ABOUT_BG} alt="אימון פרטי בבית בשוהם - אימון פונקציונלי לזוג בגיל השלישי" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="absolute inset-0 bg-[#F5F0E8]/80" />

      <div className="container relative z-10" ref={ref}>
        {/* Section title */}
        <motion.div
          initial={false}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span
            className="text-[#E8A87C] text-sm font-semibold tracking-wider uppercase mb-3 block"
            style={{ fontFamily: "var(--font-body)" }}
          >
            הסיפור שלי
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B4332]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            30 שנה של ניסיון בתנועה ובריאות
          </h2>
          <div className="w-20 h-1 bg-[#E8A87C] mx-auto mt-6 rounded-full" />
        </motion.div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Photo side */}
          <motion.div
            initial={false}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              <div className="absolute -inset-3 bg-[#2D5A3D]/8 rounded-3xl rotate-2" />
              <img
                src={SHARON_TEACHING}
                alt="שרון גולדברג - מאמנת כושר בסטודיו בשוהם"
                className="relative w-full h-[28rem] md:h-[32rem] object-cover rounded-3xl shadow-xl"
                loading="eager"
                fetchPriority="high"
                width={400}
                height={512}
              />
              {/* Decorative corner accent */}
              <div className="absolute -top-3 -right-3 w-16 h-16 border-t-4 border-r-4 border-[#E8A87C] rounded-tr-3xl" />
              <div className="absolute -bottom-3 -left-3 w-16 h-16 border-b-4 border-l-4 border-[#2D5A3D] rounded-bl-3xl" />
            </div>
          </motion.div>

          {/* Text side */}
          <motion.div
            initial={false}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <h3
              className="text-2xl md:text-3xl font-bold text-[#1B4332] mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              שרון גולדברג – כשהתנועה היא דרך חיים
            </h3>

            <div className="space-y-5 text-[#3D6B4F] text-lg leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
              <p>
                כבר למעלה משלושים שנה אני עוסקת בתחום הכושר, הבריאות והתנועה. בוגרת מכון וינגייט, עם התמחות באימון אישי, פילאטיס מזרן, אימון פונקציונלי וכושר לגיל השלישי והרביעי.
              </p>
              <p>
                לאורך השנים הבנתי שהמטרה האמיתית היא לא רק "להיכנס למכנסיים" – אלא לחיות חיים באיכות, לזוז בלי כאב, לשמור על עצמאות ולהרגיש טוב בגוף. אני מאמינה שכל אדם, בכל גיל ובכל מצב, יכול להשתפר ולהתחזק.
              </p>
              <p>
                אני מתמחה בעבודה עם אוכלוסיות מיוחדות – מבוגרים, מחלימות מסרטן, ואנשים שחוזרים לתנועה אחרי פציעה או ניתוח. הגישה שלי מבוססת על הקשבה, סבלנות, מקצועיות ואמונה בכל מתאמן ומתאמנת.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
              {stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={false}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.6 + i * 0.1, duration: 0.5 }}
                  className="text-center bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm"
                >
                  <stat.icon className="w-6 h-6 text-[#E8A87C] mx-auto mb-2" />
                  <span
                    className="text-2xl font-bold text-[#1B4332] block"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {stat.number}
                  </span>
                  <span
                    className="text-xs text-[#3D6B4F]"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {stat.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
